import { supabase } from './supabase';
import { uploadToR2, deleteFromR2, R2_FOLDERS, extractKeyFromUrl } from '../services/r2Service';

// Tipos
export interface CVGeneration {
    id: string;
    user_id: string;
    created_at: string;
    updated_at: string;

    // Nombre del CV (generado automáticamente)
    cv_name: string | null;

    // Nombre de la empresa (extraído con IA)
    company_name: string | null;

    // Preview/thumbnail del CV renderizado
    preview_image_url: string | null;

    // Assets en R2
    photo_url: string | null;
    photo_r2_key: string | null;
    original_cv_url: string | null;
    original_cv_r2_key: string | null;
    generated_pdf_url: string | null;
    generated_pdf_r2_key: string | null;

    // Datos del CV
    cv_data: any; // Objeto JSON completo

    // Contexto
    job_description: string | null;
    cover_letter: string | null;
    analysis: string | null;

    // Metadatos
    template: string | null;
    template_color: string | null;
    status: 'draft' | 'completed';
    coins_used: number;
}

/**
 * Crea una nueva generación de CV con todos los datos
 */
export async function createCVGeneration(
    userId: string,
    data: {
        cv_data: any;
        cv_name?: string;
        company_name?: string;
        preview_image_url?: string;
        job_description?: string;
        cover_letter?: string;
        analysis?: string;
        template?: string;
        template_color?: string;
        photo_url?: string;
        coins_used?: number;
        status?: 'draft' | 'completed';
    }
): Promise<{ success: boolean; data?: CVGeneration; error?: string }> {
    try {
        const { data: result, error } = await supabase
            .from('cv_generations')
            .insert({
                user_id: userId,
                cv_data: data.cv_data,
                cv_name: data.cv_name || null,
                company_name: data.company_name || null,
                preview_image_url: data.preview_image_url || null,
                job_description: data.job_description || undefined,
                cover_letter: data.cover_letter || null,
                analysis: data.analysis || null,
                template: data.template || null,
                template_color: data.template_color || null,
                photo_url: data.photo_url || undefined,
                coins_used: data.coins_used || 1,
                status: data.status || 'draft',
            })
            .select()
            .single();

        if (error) throw error;

        return { success: true, data: result };
    } catch (error) {
        console.error('Error creando generación de CV:', error);
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Error desconocido',
        };
    }
}

/**
 * Actualiza una generación de CV existente
 */
export async function updateCVGeneration(
    generationId: string,
    updates: Partial<CVGeneration>
): Promise<{ success: boolean; data?: CVGeneration; error?: string }> {
    try {
        const { data, error } = await supabase
            .from('cv_generations')
            .update({
                ...updates,
                updated_at: new Date().toISOString(),
            })
            .eq('id', generationId)
            .select()
            .single();

        if (error) throw error;

        return { success: true, data };
    } catch (error) {
        console.error('Error actualizando generación de CV:', error);
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Error desconocido',
        };
    }
}

/**
 * Obtiene todas las generaciones de CV de un usuario
 */
export async function getUserCVGenerations(
    userId: string
): Promise<{ success: boolean; data?: CVGeneration[]; error?: string }> {
    try {
        const { data, error } = await supabase
            .from('cv_generations')
            .select('*')
            .eq('user_id', userId)
            .order('created_at', { ascending: false });

        if (error) throw error;

        return { success: true, data: data || [] };
    } catch (error) {
        console.error('Error obteniendo generaciones de CV:', error);
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Error desconocido',
        };
    }
}

/**
 * Obtiene una generación específica
 */
export async function getCVGeneration(
    generationId: string
): Promise<{ success: boolean; data?: CVGeneration; error?: string }> {
    try {
        const { data, error } = await supabase
            .from('cv_generations')
            .select('*')
            .eq('id', generationId)
            .single();

        if (error) throw error;

        return { success: true, data };
    } catch (error) {
        console.error('Error obteniendo generación de CV:', error);
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Error desconocido',
        };
    }
}

/**
 * Elimina una generación de CV y sus archivos en R2
 */
export async function deleteCVGeneration(
    generationId: string
): Promise<{ success: boolean; error?: string }> {
    try {
        // Primero obtener la generación para saber qué archivos eliminar
        const { data: generation, error: fetchError } = await supabase
            .from('cv_generations')
            .select('*')
            .eq('id', generationId)
            .single();

        if (fetchError) throw fetchError;

        // Eliminar archivos de R2
        const deletePromises = [];
        if (generation.photo_r2_key) {
            deletePromises.push(deleteFromR2(generation.photo_r2_key));
        }
        if (generation.original_cv_r2_key) {
            deletePromises.push(deleteFromR2(generation.original_cv_r2_key));
        }
        if (generation.generated_pdf_r2_key) {
            deletePromises.push(deleteFromR2(generation.generated_pdf_r2_key));
        }

        await Promise.all(deletePromises);

        // Eliminar registro de la base de datos
        const { error: deleteError } = await supabase
            .from('cv_generations')
            .delete()
            .eq('id', generationId);

        if (deleteError) throw deleteError;

        return { success: true };
    } catch (error) {
        console.error('Error eliminando generación de CV:', error);
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Error desconocido',
        };
    }
}

/**
 * Sube una foto a R2 y guarda la URL en la generación
 */
export async function uploadCVPhoto(
    generationId: string,
    userId: string,
    photoFile: File
): Promise<{ success: boolean; url?: string; error?: string }> {
    try {
        // Subir a R2
        const uploadResult = await uploadToR2(photoFile, R2_FOLDERS.PHOTOS, userId);

        if (!uploadResult.success || !uploadResult.url || !uploadResult.key) {
            throw new Error(uploadResult.error || 'Error al subir foto');
        }

        // Actualizar generación
        await updateCVGeneration(generationId, {
            photo_url: uploadResult.url,
            photo_r2_key: uploadResult.key,
        });

        return {
            success: true,
            url: uploadResult.url,
        };
    } catch (error) {
        console.error('Error subiendo foto de CV:', error);
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Error desconocido',
        };
    }
}

/**
 * Sube un CV original a R2 y guarda la URL en la generación
 */
export async function uploadOriginalCV(
    generationId: string,
    userId: string,
    cvFile: File
): Promise<{ success: boolean; url?: string; error?: string }> {
    try {
        // Subir a R2
        const uploadResult = await uploadToR2(cvFile, R2_FOLDERS.CVS, userId);

        if (!uploadResult.success || !uploadResult.url || !uploadResult.key) {
            throw new Error(uploadResult.error || 'Error al subir CV');
        }

        // Actualizar generación
        await updateCVGeneration(generationId, {
            original_cv_url: uploadResult.url,
            original_cv_r2_key: uploadResult.key,
        });

        return {
            success: true,
            url: uploadResult.url,
        };
    } catch (error) {
        console.error('Error subiendo CV original:', error);
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Error desconocido',
        };
    }
}

/**
 * Genera un nombre automático para el CV
 * Formato: "CV [Título del puesto] - [Fecha]"
 */
export function generateCVName(jobTitle?: string, customDate?: Date): string {
    const title = jobTitle || 'Sin título';
    const date = customDate || new Date();

    const formattedDate = date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });

    return `CV ${title} - ${formattedDate}`;
}
